/**
 * Account Service
 * 
 * Service layer for account CRUD operations
 * Following the service layer pattern from the specification
 * Now integrated with Supabase database
 */

import { Account as AccountModelClass } from '../models/account';
import { 
  Account,
  CreateAccountRequest, 
  UpdateAccountRequest, 
  CloseAccountRequest,
  AccountListParams,
  AccountListResponse,
  Pagination,
  AccountType,
  AssetCategory,
  AssetSubType,
  ValuationSource,
  ValueBehaviour
} from '../types/account.types';
import { validateCreateAccount, validateUpdateAccount, validateCloseAccount, validateAccountListParams } from '../utils/account-validation';
import { supabaseAccountService } from './supabase-service';

export interface AccountServiceConfig {
  baseUrl: string;
  apiKey: string;
}

export class AccountService {
  private config: AccountServiceConfig;
  private useSupabase: boolean;

  constructor(config: AccountServiceConfig) {
    this.config = config;
    void this.config; // Suppress unused variable warning
    // Check if Supabase is available
    // Support both Vite (import.meta.env) and Node.js (process.env) environments
    const getEnvVar = (key: string): string | undefined => {
      if (typeof process !== 'undefined' && process.env) {
        return process.env[key];
      }
      if (typeof import.meta !== 'undefined' && import.meta.env) {
        return import.meta.env[key];
      }
      return undefined;
    };
    this.useSupabase = !!(getEnvVar('VITE_SUPABASE_URL') && getEnvVar('VITE_SUPABASE_ANON_KEY'));
  }

  /**
   * Helper function to convert Supabase account data to Account model
   */
  private convertSupabaseAccountToModel(supabaseAccount: any): AccountModelClass {
    return AccountModelClass.fromJSON({
      id: supabaseAccount.id,
      userId: supabaseAccount.user_id,
      name: supabaseAccount.name,
      type: supabaseAccount.type,
      provider: supabaseAccount.provider,
      accountNumber: supabaseAccount.account_number,
      openingBalance: supabaseAccount.opening_balance,
      currentBalance: supabaseAccount.current_balance,
      openingDate: supabaseAccount.opening_date,
      isActive: supabaseAccount.is_active,
      includeInNetWorth: supabaseAccount.include_in_net_worth,
      notes: supabaseAccount.notes,
      createdAt: supabaseAccount.created_at,
      updatedAt: supabaseAccount.updated_at,
      closureDate: supabaseAccount.closure_date,
      // closureReason is stored in notes, not as a separate field in AccountModel
      creditLimit: supabaseAccount.credit_limit,
      interestRate: supabaseAccount.interest_rate,
      paymentDueDate: supabaseAccount.payment_due_date,
      renewalDate: supabaseAccount.renewal_date,
      loanAmount: supabaseAccount.loan_amount,
      lastPaymentDue: supabaseAccount.last_payment_due,
      interestCalculatedDay: supabaseAccount.interest_calculated_day ? String(supabaseAccount.interest_calculated_day) : undefined,
      loanPurpose: supabaseAccount.loan_purpose,
      linkedAccountId: supabaseAccount.linked_account_id,
      leaseEndDate: supabaseAccount.lease_end_date,
      residualValue: supabaseAccount.residual_value,
      employerName: supabaseAccount.employer_name,
      // Asset-specific fields
      category: supabaseAccount.category,
      subType: supabaseAccount.sub_type,
      currentValue: supabaseAccount.current_value,
      purchasePrice: supabaseAccount.purchase_price,
      purchaseDate: supabaseAccount.purchase_date,
      valuationSource: supabaseAccount.valuation_source,
      valuationProvider: supabaseAccount.valuation_provider,
      lastValuationDate: supabaseAccount.last_valuation_date,
      valueBehaviour: supabaseAccount.value_behaviour,
      depreciationMethod: supabaseAccount.depreciation_method,
      depreciationRatePercent: supabaseAccount.depreciation_rate_percent,
      autoUpdateValue: supabaseAccount.auto_update_value
    });
  }

  /**
   * Helper function to convert Account model to Account interface
   */
  private convertAccountModelToInterface(accountModel: AccountModelClass): Account {
    const accountData = accountModel.toJSON();
    return {
      ...accountData,
      interestCalculatedDay: accountData.interestCalculatedDay ? Number(accountData.interestCalculatedDay) : undefined,
    } as Account;
  }

  // CRUD Operations

  /**
   * Create a new account
   */
  async createAccount(userId: string, accountData: CreateAccountRequest): Promise<Account> {
    // Validate input
    const validation = validateCreateAccount(accountData);
    if (!validation.success) {
      throw new Error(`Validation failed: ${validation.error.issues.map(i => i.message).join(', ')}`);
    }

    // Additional asset validation
    if (accountData.type === AccountType.ASSETS) {
      const assetValidation = this.validateAsset(accountData);
      if (!assetValidation.valid) {
        throw new Error(`Asset validation failed: ${assetValidation.errors.join(', ')}`);
      }
    }

    if (this.useSupabase) {
      // Use Supabase for persistence
      try {
        // Set default values for assets
        let lastValuationDate = accountData.lastValuationDate;
        if (accountData.type === AccountType.ASSETS && !lastValuationDate) {
          lastValuationDate = accountData.purchaseDate || accountData.openingDate;
        }

        let valueBehaviour: ValueBehaviour | undefined;
        if (accountData.type === AccountType.ASSETS && accountData.category) {
          valueBehaviour = this.getDefaultValueBehaviour(accountData.category);
        }

        // For assets, map purchaseDate to openingDate if openingDate is not provided
        // This allows CreateAssetRequest to use purchaseDate without requiring openingDate
        const effectiveOpeningDate = accountData.type === AccountType.ASSETS && !accountData.openingDate && accountData.purchaseDate
          ? accountData.purchaseDate
          : accountData.openingDate;

        const supabaseAccount = await supabaseAccountService.createAccount({
          userId,
          name: accountData.name,
          type: accountData.type,
          provider: accountData.provider,
          accountNumber: accountData.accountNumber,
          openingBalance: accountData.openingBalance,
          currentBalance: accountData.openingBalance,
          openingDate: effectiveOpeningDate,
          isActive: true,
          includeInNetWorth: accountData.includeInNetWorth ?? true,
          notes: accountData.notes,
          creditLimit: accountData.creditLimit,
          interestRate: accountData.interestRate,
          paymentDueDate: accountData.paymentDueDate,
          renewalDate: accountData.renewalDate,
          loanAmount: accountData.loanAmount,
          lastPaymentDue: accountData.lastPaymentDue,
          interestCalculatedDay: accountData.interestCalculatedDay,
          loanPurpose: accountData.loanPurpose,
          linkedAccountId: accountData.linkedAccountId,
          leaseEndDate: accountData.leaseEndDate,
          residualValue: accountData.residualValue,
          employerName: accountData.employerName,
          // Asset-specific fields
          category: accountData.category,
          subType: accountData.subType,
          currentValue: accountData.currentValue,
          purchasePrice: accountData.purchasePrice,
          purchaseDate: accountData.purchaseDate,
          valuationSource: accountData.valuationSource || ValuationSource.MANUAL,
          lastValuationDate: lastValuationDate,
          valueBehaviour: accountData.valueBehaviour || valueBehaviour,
          depreciationMethod: accountData.depreciationMethod,
          depreciationRatePercent: accountData.depreciationRatePercent,
          autoUpdateValue: accountData.autoUpdateValue || false
        });

        // Convert Supabase account to Account model, then to Account interface
        const accountModel = this.convertSupabaseAccountToModel(supabaseAccount);
        return this.convertAccountModelToInterface(accountModel);
      } catch (error) {
        throw new Error(`Failed to create account in database: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      // Fallback to in-memory storage for development/testing
      const account = AccountModelClass.create({
        userId,
        name: accountData.name,
        type: accountData.type,
        provider: accountData.provider,
        accountNumber: accountData.accountNumber,
        openingBalance: accountData.openingBalance,
        currentBalance: accountData.openingBalance,
        openingDate: accountData.openingDate,
        isActive: true,
        includeInNetWorth: accountData.includeInNetWorth ?? true,
        notes: accountData.notes,
        
        // Type-specific fields
        creditLimit: accountData.creditLimit,
        interestRate: accountData.interestRate,
        paymentDueDate: accountData.paymentDueDate,
        renewalDate: accountData.renewalDate,
        loanAmount: accountData.loanAmount,
        lastPaymentDue: accountData.lastPaymentDue,
        interestCalculatedDay: accountData.interestCalculatedDay ? String(accountData.interestCalculatedDay) : undefined,
        loanPurpose: accountData.loanPurpose,
        linkedAccountId: accountData.linkedAccountId,
        leaseEndDate: accountData.leaseEndDate,
        residualValue: accountData.residualValue,
        employerName: accountData.employerName,
        
        // Asset-specific fields
        category: accountData.category,
        subType: accountData.subType,
        currentValue: accountData.currentValue,
        purchasePrice: accountData.purchasePrice,
        purchaseDate: accountData.purchaseDate,
        valuationSource: accountData.valuationSource || ValuationSource.MANUAL,
        lastValuationDate: accountData.lastValuationDate || accountData.purchaseDate || accountData.openingDate,
        valueBehaviour: accountData.valueBehaviour || (accountData.category ? this.getDefaultValueBehaviour(accountData.category) : undefined),
        depreciationMethod: accountData.depreciationMethod,
        depreciationRatePercent: accountData.depreciationRatePercent,
        autoUpdateValue: accountData.autoUpdateValue || false
      });

      // Validate account
      const errors = account.validate();
      if (errors.length > 0) {
        throw new Error(`Account validation failed: ${errors.join(', ')}`);
      }

      return this.convertAccountModelToInterface(account);
    }
  }

  /**
   * Get account by ID
   */
  async getAccountById(accountId: string, userId: string): Promise<Account | null> {
    if (this.useSupabase) {
      try {
        const supabaseAccount = await supabaseAccountService.getAccountById(accountId, userId);
        if (!supabaseAccount) {
          return null;
        }

        // Convert Supabase account to Account model, then to Account interface
        const accountModel = this.convertSupabaseAccountToModel(supabaseAccount);
        return this.convertAccountModelToInterface(accountModel);
      } catch (error) {
        console.error('Error fetching account from Supabase:', error);
        return null;
      }
    } else {
      // Fallback to in-memory storage
      return null; // This would need to be implemented if not using Supabase
    }
  }

  /**
   * Get all accounts for a user
   */
  async getAccounts(userId: string, params: AccountListParams = {}): Promise<AccountListResponse> {
    // Validate params
    const validation = validateAccountListParams(params);
    if (!validation.success) {
      throw new Error(`Validation failed: ${validation.error.issues.map(i => i.message).join(', ')}`);
    }

    const validatedParams = validation.data;

    if (this.useSupabase) {
      try {
        const result = await supabaseAccountService.getAccounts(userId, {
          type: validatedParams.type,
          status: validatedParams.status as 'active' | 'closed' | undefined,
          includeInNetWorth: validatedParams.includeInNetWorth,
          page: validatedParams.page,
          limit: validatedParams.limit
        });

        // Convert Supabase accounts to Account models, then to Account interfaces
        const accounts = result.accounts.map(supabaseAccount => {
          const accountModel = this.convertSupabaseAccountToModel(supabaseAccount);
          return this.convertAccountModelToInterface(accountModel);
        });

        const pagination: Pagination = {
          page: validatedParams.page,
          limit: validatedParams.limit,
          total: result.total,
          totalPages: Math.ceil(result.total / validatedParams.limit),
          hasNext: validatedParams.page < Math.ceil(result.total / validatedParams.limit),
          hasPrev: validatedParams.page > 1
        };

        return {
          accounts, // accounts are already Account[] interfaces
          pagination
        };
      } catch (error) {
        throw new Error(`Failed to fetch accounts from database: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      // Fallback to in-memory storage (simplified for development)
      return {
        accounts: [],
        pagination: {
          page: validatedParams.page,
          limit: validatedParams.limit,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        }
      };
    }
  }

  /**
   * Update an account
   */
  async updateAccount(accountId: string, userId: string, updateData: UpdateAccountRequest): Promise<Account | null> {
    
    // Validate input
    const validation = validateUpdateAccount(updateData);
    
    
    if (!validation.success) {
      throw new Error(`Validation failed: ${validation.error.issues.map(i => i.message).join(', ')}`);
    }

    // Additional asset validation if updating asset fields
    if (updateData.category !== undefined || updateData.subType !== undefined || updateData.currentValue !== undefined) {
      const assetValidation = this.validateAsset(updateData);
      if (!assetValidation.valid) {
        throw new Error(`Asset validation failed: ${assetValidation.errors.join(', ')}`);
      }
    }

    if (this.useSupabase) {
      try {
        // For assets, keep openingDate in sync with purchaseDate
        const effectiveOpeningDate =
          updateData.purchaseDate && !updateData.openingDate
            ? updateData.purchaseDate
            : updateData.openingDate;

        const supabaseAccount = await supabaseAccountService.updateAccount(accountId, userId, {
          name: updateData.name,
          type: updateData.type,
          provider: updateData.provider,
          accountNumber: updateData.accountNumber,
          openingBalance: updateData.openingBalance,
          currentBalance: updateData.currentBalance,
          openingDate: effectiveOpeningDate,
          isActive: updateData.isActive,
          includeInNetWorth: updateData.includeInNetWorth,
          notes: updateData.notes,
          closureDate: updateData.closureDate,
          closureReason: updateData.closureReason,
          creditLimit: updateData.creditLimit,
          interestRate: updateData.interestRate,
          paymentDueDate: updateData.paymentDueDate,
          renewalDate: updateData.renewalDate,
          loanAmount: updateData.loanAmount,
          lastPaymentDue: updateData.lastPaymentDue,
          interestCalculatedDay: updateData.interestCalculatedDay,
          loanPurpose: updateData.loanPurpose,
          linkedAccountId: updateData.linkedAccountId,
          leaseEndDate: updateData.leaseEndDate,
          residualValue: updateData.residualValue,
          employerName: updateData.employerName,
          // Asset-specific fields
          category: updateData.category,
          subType: updateData.subType,
          currentValue: updateData.currentValue,
          purchasePrice: updateData.purchasePrice,
          purchaseDate: updateData.purchaseDate,
          valuationSource: updateData.valuationSource,
          valuationProvider: updateData.valuationProvider,
          lastValuationDate: updateData.lastValuationDate,
          valueBehaviour: updateData.valueBehaviour,
          depreciationMethod: updateData.depreciationMethod,
          depreciationRatePercent: updateData.depreciationRatePercent,
          autoUpdateValue: updateData.autoUpdateValue
        });

        if (!supabaseAccount) {
          return null;
        }


        // Convert Supabase account to Account model, then to Account interface
        const accountModel = this.convertSupabaseAccountToModel(supabaseAccount);
        
        
        const result = this.convertAccountModelToInterface(accountModel);
        
        
        return result;
      } catch (error) {
        throw new Error(`Failed to update account in database: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      // Fallback to in-memory storage
      return null; // This would need to be implemented if not using Supabase
    }
  }

  /**
   * Close an account
   */
  async closeAccount(accountId: string, userId: string, closeData: CloseAccountRequest): Promise<Account | null> {
    // Validate input
    const validation = validateCloseAccount(closeData);
    if (!validation.success) {
      throw new Error(`Validation failed: ${validation.error.issues.map(i => i.message).join(', ')}`);
    }

    if (this.useSupabase) {
      try {
        const supabaseAccount = await supabaseAccountService.closeAccount(accountId, userId, {
          closureDate: closeData.closureDate,
          closureReason: closeData.closureReason,
          includeInNetWorth: closeData.includeInNetWorth,
          currentValue: closeData.currentValue
        });

        if (!supabaseAccount) {
          return null;
        }

        // Convert Supabase account to Account model, then to Account interface
        const accountModel = this.convertSupabaseAccountToModel(supabaseAccount);
        return this.convertAccountModelToInterface(accountModel);
      } catch (error) {
        throw new Error(`Failed to close account in database: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      // Fallback to in-memory storage
      return null; // This would need to be implemented if not using Supabase
    }
  }

  /**
   * Delete an account
   */
  async deleteAccount(accountId: string, userId: string): Promise<boolean> {
    if (this.useSupabase) {
      try {
        await supabaseAccountService.deleteAccount(accountId, userId);
        return true;
      } catch (error) {
        throw new Error(`Failed to delete account from database: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      // Fallback to in-memory storage
      return false; // This would need to be implemented if not using Supabase
    }
  }

  // Asset validation helper functions

  /**
   * Validate asset data
   */
  validateAsset(assetData: CreateAccountRequest | UpdateAccountRequest): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (assetData.type === AccountType.ASSETS || 'category' in assetData) {
      // Validate category
      if (!assetData.category) {
        errors.push('Category is required for asset accounts');
      }

      // Validate sub-type
      if (!assetData.subType) {
        errors.push('Sub-type is required for asset accounts');
      }

      // Validate category/sub-type matching
      if (assetData.category && assetData.subType) {
        if (!this.isValidSubTypeForCategory(assetData.category, assetData.subType)) {
          errors.push(`Sub-type '${assetData.subType}' is not valid for category '${assetData.category}'`);
        }
      }

      // Validate current value
      if (assetData.currentValue === undefined || assetData.currentValue === null) {
        errors.push('Current value is required for asset accounts');
      }
      // Note: Negative values are allowed per FR-027 to represent liabilities or depreciated assets

      // Validate purchase date
      if (assetData.purchaseDate) {
        const purchaseDate = new Date(assetData.purchaseDate);
        const today = new Date();
        if (purchaseDate > today) {
          errors.push('Purchase date cannot be in the future');
        }
      }

      // Validate last valuation date
      if (assetData.lastValuationDate) {
        const lastValuationDate = new Date(assetData.lastValuationDate);
        if (isNaN(lastValuationDate.getTime())) {
          errors.push('Last valuation date must be a valid date');
        }
      }

      // Validate depreciation rate
      if (assetData.depreciationRatePercent !== undefined) {
        if (assetData.depreciationRatePercent < 0 || assetData.depreciationRatePercent > 100) {
          errors.push('Depreciation rate must be between 0 and 100');
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Check if sub-type is valid for a given category
   */
  isValidSubTypeForCategory(category: AssetCategory, subType: AssetSubType): boolean {
    const validSubTypes = this.getSubTypesForCategory(category);
    return validSubTypes.includes(subType);
  }

  /**
   * Get valid sub-types for a category
   */
  getSubTypesForCategory(category: AssetCategory): AssetSubType[] {
    const subTypeMap: Record<AssetCategory, AssetSubType[]> = {
      [AssetCategory.PROPERTY]: [AssetSubType.HOUSE, AssetSubType.APARTMENT, AssetSubType.LAND, AssetSubType.OTHER],
      [AssetCategory.VEHICLE]: [AssetSubType.CAR, AssetSubType.MOTORBIKE, AssetSubType.BOAT, AssetSubType.OTHER],
      [AssetCategory.VALUABLES]: [AssetSubType.JEWELLERY, AssetSubType.ART, AssetSubType.COLLECTIBLES, AssetSubType.OTHER],
      [AssetCategory.OTHER]: [AssetSubType.OTHER]
    };

    return subTypeMap[category] || [];
  }

  /**
   * Get default value behaviour based on category
   */
  getDefaultValueBehaviour(category: AssetCategory): ValueBehaviour {
    // Property typically appreciates, vehicles depreciate, valuables can be mixed
    const defaultBehaviourMap: Record<AssetCategory, ValueBehaviour> = {
      [AssetCategory.PROPERTY]: ValueBehaviour.APPRECIATES,
      [AssetCategory.VEHICLE]: ValueBehaviour.DEPRECIATES,
      [AssetCategory.VALUABLES]: ValueBehaviour.MIXED,
      [AssetCategory.OTHER]: ValueBehaviour.MIXED
    };

    return defaultBehaviourMap[category] || ValueBehaviour.MIXED;
  }

  /**
   * Get assets included in net worth
   */
  async getNetWorthAssets(userId: string): Promise<Account[]> {
    const result = await this.getAccounts(userId, { 
      type: AccountType.ASSETS, 
      includeInNetWorth: true 
    });
    // result.accounts is already Account[] from account.types.ts, return as-is
    return result.accounts;
  }

  // Utility methods - simplified for Supabase integration

  /**
   * Get accounts by type
   */
  async getAccountsByType(userId: string, type: AccountType): Promise<Account[]> {
    const result = await this.getAccounts(userId, { type });
    // result.accounts is already Account[] from account.types.ts, return as-is
    return result.accounts;
  }

  /**
   * Get active accounts
   */
  async getActiveAccounts(userId: string): Promise<Account[]> {
    const result = await this.getAccounts(userId, { status: 'active' });
    // result.accounts is already Account[] from account.types.ts, return as-is
    return result.accounts;
  }

  /**
   * Get closed accounts
   */
  async getClosedAccounts(userId: string): Promise<Account[]> {
    const result = await this.getAccounts(userId, { status: 'closed' });
    // result.accounts is already Account[] from account.types.ts, return as-is
    return result.accounts;
  }

  /**
   * Get accounts included in net worth
   */
  async getNetWorthAccounts(userId: string): Promise<Account[]> {
    const result = await this.getAccounts(userId, { includeInNetWorth: true });
    // result.accounts is already Account[] from account.types.ts, return as-is
    return result.accounts;
  }

  /**
   * Calculate total net worth (including assets)
   */
  async calculateNetWorth(userId: string): Promise<number> {
    const netWorthAccounts = await this.getNetWorthAccounts(userId);
    const accountBalance = netWorthAccounts
      .filter(account => account.type !== AccountType.ASSETS)
      .reduce((total, account) => total + account.currentBalance, 0);
    
    const assets = await this.getNetWorthAssets(userId);
    const assetValue = assets.reduce((total, asset) => total + (asset.currentValue || 0), 0);
    
    return accountBalance + assetValue;
  }

  /**
   * Get account statistics
   */
  async getAccountStatistics(userId: string): Promise<{
    totalAccounts: number;
    activeAccounts: number;
    closedAccounts: number;
    totalBalance: number;
    netWorth: number;
    accountsByType: Record<AccountType, number>;
  }> {
    const allAccountsResult = await this.getAccounts(userId);
    const allAccounts = allAccountsResult.accounts;

    const activeAccounts = allAccounts.filter(account => account.isActive);
    const closedAccounts = allAccounts.filter(account => account.closureDate != null);
    const netWorthAccounts = allAccounts.filter(account => account.includeInNetWorth);

    const totalBalance = allAccounts.reduce((total, account) => total + account.currentBalance, 0);
    const netWorth = netWorthAccounts.reduce((total, account) => total + account.currentBalance, 0);

    const accountsByType = allAccounts.reduce((acc, account) => {
      acc[account.type] = (acc[account.type] || 0) + 1;
      return acc;
    }, {} as Record<AccountType, number>);

    return {
      totalAccounts: allAccounts.length,
      activeAccounts: activeAccounts.length,
      closedAccounts: closedAccounts.length,
      totalBalance,
      netWorth,
      accountsByType
    };
  }
}
